import React from 'react';
import { 
  Box, 
  Zap, 
  Shield, 
  Smartphone, 
  Globe, 
  Database, 
  CheckCircle2, 
  ArrowRight,
  Play,
  Layers,
  Users,
  MessageSquare
} from 'lucide-react';
import { motion } from 'motion/react';

export const FAModule: React.FC = () => {
  const steps = [
    { title: 'B2B Sync', desc: 'Auto-sync catalog & retailers from RiEtail B2B.', icon: Database, color: 'text-blue-500' },
    { title: 'Rep Setup', desc: 'Create field rep accounts with secure access.', icon: Users, color: 'text-purple-500' },
    { title: 'Assignment', desc: 'Map retailers & products to specific reps.', icon: Layers, color: 'text-indigo-500' },
    { title: 'Scheduling', desc: 'Plan daily routes & optimized visit paths.', icon: Globe, color: 'text-emerald-500' },
    { title: 'Field Action', desc: 'Reps book orders via the Field Assist App.', icon: Smartphone, color: 'text-amber-500' },
    { title: 'Confirmation', desc: 'Real-time B2B order creation & tracking.', icon: Zap, color: 'text-primary' },
  ];

  return (
    <div className="space-y-12 pb-20">
      {/* Hero Section */}
      <div className="relative h-[400px] rounded-[40px] overflow-hidden bg-[#1C2B3A] flex items-center px-12">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
          <div className="grid grid-cols-10 gap-4 p-8">
            {Array.from({ length: 50 }).map((_, i) => (
              <div key={i} className="h-20 border border-white/5 rounded-xl" />
            ))}
          </div>
        </div>
        
        <div className="relative z-10 max-w-2xl space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/30 rounded-full">
            <Zap size={16} className="text-primary" />
            <span className="text-xs font-bold text-primary uppercase tracking-widest">Field Assist Module</span>
          </div>
          <h1 className="text-5xl font-black text-white leading-tight">
            The Power of B2B, <br />
            <span className="text-primary">In the Field.</span>
          </h1>
          <p className="text-lg text-white/70 font-medium leading-relaxed">
            Bridge the gap between your warehouse and the retailer. Field Assist empowers your sales team with real-time B2B catalog access, smart routing, and instant order synchronization.
          </p>
          <div className="flex gap-4">
            <button className="btn-primary h-14 px-8 text-lg">Get Started â†’</button>
            <button className="h-14 px-8 border border-white/20 rounded-2xl text-white font-bold hover:bg-white/5 transition-all flex items-center gap-3">
              <Play size={20} fill="currentColor" />
              Watch Demo
            </button>
          </div>
        </div>

        {/* Floating Phone Simulation */}
        <div className="absolute right-12 top-1/2 -translate-y-1/2 w-[280px] h-[540px] bg-[#0F172A] rounded-[48px] border-[8px] border-[#334155] shadow-2xl hidden xl:block">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#334155] rounded-b-2xl" />
          <div className="p-6 pt-12 space-y-6">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs">FA</div>
              <div className="flex gap-1">
                <div className="w-1 h-1 rounded-full bg-white/40" />
                <div className="w-1 h-1 rounded-full bg-white/40" />
                <div className="w-1 h-1 rounded-full bg-white/40" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-4 w-24 bg-white/10 rounded-full" />
              <div className="h-8 w-48 bg-white/20 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-32 bg-white/5 rounded-2xl border border-white/10" />
              <div className="h-32 bg-white/5 rounded-2xl border border-white/10" />
            </div>
            <div className="h-40 bg-gradient-to-br from-primary/40 to-purple-600/40 rounded-3xl border border-primary/30 flex items-center justify-center">
              <CheckCircle2 size={48} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Steps Flow */}
      <div className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-black text-text">How it Works</h2>
          <p className="text-text2">A seamless 6-step flow from B2B sync to field delivery.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, i) => (
            <div key={i} className="card p-8 group hover:border-primary transition-all relative overflow-hidden">
              <div className="absolute -right-4 -bottom-4 text-8xl font-black text-gray-50 group-hover:text-primary/5 transition-colors">
                {i + 1}
              </div>
              <div className={`w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-6 ${step.color} group-hover:bg-primary group-hover:text-white transition-all shadow-sm`}>
                <step.icon size={28} />
              </div>
              <h3 className="text-xl font-black text-text mb-2">{step.title}</h3>
              <p className="text-sm text-text2 leading-relaxed relative z-10">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-12">
        <div className="space-y-8">
          <h2 className="text-4xl font-black text-text leading-tight">
            Built for Scale, <br />
            Designed for <span className="text-primary">Speed.</span>
          </h2>
          <div className="space-y-6">
            {[
              { title: 'Real-time B2B Sync', desc: 'Never sell out-of-stock items. Field Assist syncs inventory every 60 seconds.', icon: Zap },
              { title: 'WhatsApp Notifications', desc: 'Automatically send order confirmations to retailers via WhatsApp Business.', icon: MessageSquare },
              { title: 'Offline Mode', desc: 'Reps can book orders even in low-connectivity areas. Syncs when back online.', icon: Globe },
              { title: 'Secure & Encrypted', desc: 'Enterprise-grade security for all your business data and transactions.', icon: Shield },
            ].map((feat, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-primary shrink-0">
                  <feat.icon size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-text mb-1">{feat.title}</h4>
                  <p className="text-sm text-text2 leading-relaxed">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-8 bg-gray-50 border-border/50">
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-black text-text">Integration Status</h3>
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase">All Systems Operational</span>
          </div>
          <div className="space-y-4">
            {[
              { name: 'RiEtail B2B Core', status: 'Connected', color: 'bg-emerald-500' },
              { name: 'WhatsApp API', status: 'Connected', color: 'bg-emerald-500' },
              { name: 'Payment Gateway', status: 'Active', color: 'bg-emerald-500' },
              { name: 'Field User App', status: 'v2.4.0 Live', color: 'bg-blue-500' },
              { name: 'Analytics Engine', status: 'Processing', color: 'bg-indigo-500' },
            ].map((sys, i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-white rounded-2xl border border-border/50 shadow-sm">
                <span className="text-sm font-bold text-text">{sys.name}</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${sys.color} animate-pulse`} />
                  <span className="text-xs font-bold text-text2 uppercase tracking-tight">{sys.status}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-8 h-12 bg-white border border-border rounded-xl text-sm font-bold text-text2 hover:bg-gray-100 transition-all flex items-center justify-center gap-2">
            View API Documentation
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
